import { useEffect, useMemo, useState } from "react";
import {
  CircleNotch,
  FilePdf,
  FilmSlate,
  MagnifyingGlass,
  X,
  Check,
} from "@phosphor-icons/react";
import { listMedia } from "@/lib/api";

/**
 * Modal per scegliere un file già presente nella libreria media.
 * Props:
 *   - kind: 'image' | 'pdf' | 'video'  (filtra i file mostrati)
 *   - onSelect: (url: string) => void
 *   - onClose: () => void
 */
export default function MediaPickerModal({ kind, onSelect, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    listMedia({ kind })
      .then((r) => setItems(r.items || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [kind]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) =>
      (m.orig_name || m.filename || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const selected = filtered.find((m) => m.id === selectedId);

  const confirm = () => {
    if (selected) {
      onSelect(selected.url);
      onClose();
    }
  };

  const kindLabel =
    kind === "image" ? "immagine" : kind === "pdf" ? "PDF" : "video";

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#1A1918]/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#FAF9F6] w-full max-w-4xl max-h-[85vh] flex flex-col border border-[#D1CFC7] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="media-picker-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 md:p-6 border-b border-[#D1CFC7]/60">
          <div>
            <div className="overline mb-1">Libreria media</div>
            <h3 className="font-display text-2xl tracking-tighter font-medium">
              Scegli {kindLabel === "immagine" ? "un'" : "un "}
              {kindLabel}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#F0EFEB] transition-colors"
            aria-label="Chiudi"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Search */}
        <div className="p-5 md:p-6 border-b border-[#D1CFC7]/60">
          <div className="relative">
            <MagnifyingGlass
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5A55]"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome file…"
              className="w-full pl-9 pr-3 py-2.5 bg-[#F0EFEB] border border-[#D1CFC7] text-sm focus:outline-none focus:border-[#1A1918]"
              data-testid="media-picker-search"
            />
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6">
          {error && (
            <div className="p-3 bg-[#EB0D0D]/8 text-[#C00A0A] text-sm border border-[#EB0D0D]/30 mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <CircleNotch
                size={28}
                weight="bold"
                className="animate-spin text-[#EB0D0D]"
              />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-[#F0EFEB] border border-[#D1CFC7]/40 py-16 text-center text-[#5C5A55]">
              {items.length === 0
                ? `Nessun ${kindLabel} nella libreria. Carica prima un file dalla scheda «Media».`
                : "Nessun risultato per questa ricerca."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map((m) => (
                <PickerCard
                  key={m.id}
                  item={m}
                  selected={selectedId === m.id}
                  onClick={() => setSelectedId(m.id)}
                  onDoubleClick={() => {
                    onSelect(m.url);
                    onClose();
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-5 md:p-6 border-t border-[#D1CFC7]/60 bg-[#F0EFEB]">
          <div className="text-xs text-[#5C5A55] truncate">
            {selected
              ? `Selezionato: ${selected.orig_name || selected.filename}`
              : "Clicca su un file per selezionarlo (doppio clic per confermare)"}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-transparent text-[#1A1918] border border-[#D1CFC7] hover:border-[#1A1918] transition-colors"
            >
              Annulla
            </button>
            <button
              onClick={confirm}
              disabled={!selected}
              data-testid="media-picker-confirm"
              className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold bg-[#EB0D0D] text-[#FAF9F6] hover:bg-[#C00A0A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <Check size={14} weight="bold" />
              Usa questo file
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PickerCard({ item, selected, onClick, onDoubleClick }) {
  const isImage = item.kind === "image";
  const isVideo = item.kind === "video";
  const displayName = item.orig_name || item.filename;

  return (
    <button
      type="button"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      data-testid={`media-picker-item-${item.id}`}
      className={`text-left bg-[#F0EFEB] border transition-colors flex flex-col ${
        selected
          ? "border-[#EB0D0D] ring-2 ring-[#EB0D0D]/30"
          : "border-[#D1CFC7]/40 hover:border-[#1A1918]"
      }`}
    >
      <div className="relative aspect-square bg-[#FAF9F6] flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={item.url}
            alt={displayName}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : isVideo ? (
          <>
            <video
              src={item.url}
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-[#1A1918]/30 pointer-events-none">
              <FilmSlate size={32} weight="fill" className="text-[#FAF9F6]" />
            </div>
          </>
        ) : (
          <FilePdf size={48} weight="duotone" className="text-[#EB0D0D]" />
        )}
        {selected && (
          <div className="absolute top-1.5 right-1.5 p-1 bg-[#EB0D0D] text-[#FAF9F6]">
            <Check size={12} weight="bold" />
          </div>
        )}
      </div>
      <div
        className="p-2 text-[11px] text-[#1A1918] truncate"
        title={displayName}
      >
        {displayName}
      </div>
    </button>
  );
}
